import { useQuery } from '@tanstack/react-query'
import type { ApiResponse } from '@/lib/api'
import { useApi } from '@/hooks/use-api'

// --- Types ---

export type TrendDirection = 'up' | 'down' | 'neutral'

export type Trend = {
  readonly value: number
  readonly direction: TrendDirection
}

export type DashboardStats = {
  readonly totalLeads: number
  readonly replyRate: number
  readonly meetingsBooked: number
  readonly contentEngagement: number
  readonly trends: {
    readonly totalLeads: Trend
    readonly replyRate: Trend
    readonly meetingsBooked: Trend
    readonly contentEngagement: Trend
  }
}

export type ActivityType = 'connection' | 'reply' | 'post' | 'enrichment'

export type ActivityItem = {
  readonly id: string
  readonly type: ActivityType
  readonly description: string
  readonly timestamp: string
  readonly entityUrl?: string
}

export type PipelineStage = {
  readonly name: string
  readonly count: number
  readonly percentage: number
}

// --- Backend response shapes ---

// Shape returned by GET /api/leads/stats (via leadsDb.getStats)
type LeadsStatsResponse = {
  readonly total?: number
  readonly by_status?: Partial<Record<string, number>>
  readonly avg_icp_score?: number
  readonly avg_intent_score?: number
  readonly avg_composite_score?: number
}

type PipelineResponse = readonly PipelineStage[]

// --- Placeholder data ---

const EMPTY_STATS: DashboardStats = {
  totalLeads: 0,
  replyRate: 0,
  meetingsBooked: 0,
  contentEngagement: 0,
  trends: {
    totalLeads: { value: 0, direction: 'neutral' },
    replyRate: { value: 0, direction: 'neutral' },
    meetingsBooked: { value: 0, direction: 'neutral' },
    contentEngagement: { value: 0, direction: 'neutral' },
  },
}

const NEUTRAL_TREND: Trend = { value: 0, direction: 'neutral' }

// --- Fetcher helpers (accept apiFetch as parameter) ---

async function fetchStats(
  apiFetch: <T>(path: string) => Promise<ApiResponse<T>>,
): Promise<DashboardStats> {
  const res = await apiFetch<LeadsStatsResponse>('/api/leads/stats')
  if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load dashboard stats')
  const d = res.data
  const byStatus = d.by_status ?? {}
  const totalLeads = d.total ?? 0

  // Derive reply rate from lead statuses (replied + qualified + meeting_booked / total contacted)
  const totalContacted =
    (byStatus['contacted'] ?? 0) +
    (byStatus['replied'] ?? 0) +
    (byStatus['qualified'] ?? 0) +
    (byStatus['meeting_booked'] ?? 0)
  const totalReplied = (byStatus['replied'] ?? 0) + (byStatus['qualified'] ?? 0) + (byStatus['meeting_booked'] ?? 0)
  const replyRate = totalContacted > 0 ? Math.round((totalReplied / totalContacted) * 1000) / 10 : 0

  const meetingsBooked = byStatus['meeting_booked'] ?? 0

  return {
    totalLeads,
    replyRate,
    meetingsBooked,
    contentEngagement: 0, // populated separately by content worker stats
    trends: {
      totalLeads: NEUTRAL_TREND,
      replyRate: NEUTRAL_TREND,
      meetingsBooked: NEUTRAL_TREND,
      contentEngagement: NEUTRAL_TREND,
    },
  }
}

// Shape returned by GET /api/leads/activity
type ActivityLogEntry = {
  readonly id: string
  readonly action_type: string
  readonly entity_type: string | null
  readonly entity_id: string | null
  readonly details: string | null
  readonly created_at: string
}

async function fetchActivity(
  apiFetch: <T>(path: string) => Promise<ApiResponse<T>>,
  limit = 20,
): Promise<readonly ActivityItem[]> {
  const res = await apiFetch<{ readonly data: readonly ActivityLogEntry[]; readonly total: number }>(
    `/api/leads/activity?limit=${limit}`,
  )
  if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load activity feed')
  const entries = res.data.data ?? []
  return entries
    .map((entry) => mapActivityEntry(entry))
    .filter((item): item is ActivityItem => item !== null)
}

async function fetchPipeline(
  apiFetch: <T>(path: string) => Promise<ApiResponse<T>>,
): Promise<readonly PipelineStage[]> {
  const res = await apiFetch<PipelineResponse>('/api/leads/pipeline')
  if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load pipeline data')
  return res.data
}

// Allowlist of action_types the dashboard surfaces. Defensive — backend
// already filters these — but if a new error type leaks through we'd rather
// drop it than render it as a generic "connection" event.
const TYPE_MAP: Record<string, ActivityType> = {
  connection_sent: 'connection',
  connection_accepted: 'connection',
  reply_received: 'reply',
  lead_qualified: 'reply',
  meeting_booked: 'reply',
  post_published: 'post',
  lead_enriched: 'enrichment',
  lead_created: 'enrichment',
  message_sent: 'reply',
  linkedin_connected: 'connection',
  linkedin_disconnected: 'connection',
}

// Map activity_log DB entry → ActivityItem for the dashboard feed.
// Returns null for unknown/system action types so they don't pollute the UI.
function mapActivityEntry(entry: ActivityLogEntry): ActivityItem | null {
  const type = TYPE_MAP[entry.action_type]
  if (!type) return null

  let description = entry.action_type.replace(/_/g, ' ')
  if (entry.details) {
    try {
      const details = JSON.parse(entry.details) as Record<string, unknown>
      if (typeof details.description === 'string') {
        description = details.description
      } else if (typeof details.lead_name === 'string') {
        description = `${entry.action_type.replace(/_/g, ' ')} — ${details.lead_name}`
      }
    } catch {
      // ignore JSON parse errors
    }
  }

  return {
    id: entry.id,
    type,
    description,
    timestamp: entry.created_at,
    entityUrl: entry.entity_id
      ? `/leads/${entry.entity_id}`
      : undefined,
  }
}

// --- Hooks ---

export function useDashboardStats() {
  const apiFetch = useApi()

  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => fetchStats(apiFetch),
    refetchInterval: 30_000,
    placeholderData: EMPTY_STATS,
  })
}

export function useDashboardActivity(limit = 20) {
  const apiFetch = useApi()

  return useQuery({
    queryKey: ['dashboard', 'activity', limit],
    queryFn: () => fetchActivity(apiFetch, limit),
    refetchInterval: 30_000,
  })
}

export function useDashboardPipeline() {
  const apiFetch = useApi()

  return useQuery({
    queryKey: ['dashboard', 'pipeline'],
    queryFn: () => fetchPipeline(apiFetch),
    refetchInterval: 30_000,
  })
}

export function useIsEmptyDashboard(stats: DashboardStats | undefined): boolean {
  if (!stats) return true
  return (
    stats.totalLeads === 0 &&
    stats.meetingsBooked === 0 &&
    stats.contentEngagement === 0
  )
}
